import React, { useEffect, useRef, useState } from "react";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaMapMarkerAlt,
  FaPhone,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import background from "../image/svg/footer.svg";
import TestimonialSlider from "./Testimonialslider";
import CallToAction from "./CallTOAction";
import ServiceSection from "./servicesection";

const Webfooter = () => {
  const footerRef = useRef(null);
  const location = useLocation();

  const [openSection, setOpenSection] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [footerMargin, setFooterMargin] = useState("0px");

  // ✅ Update footer margin based on current page (dynamic matching)
  useEffect(() => {
    const updateMargin = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);

      if (width <= 768) {
        if (location.pathname.startsWith("/cart")) setFooterMargin("80px");
        else if (location.pathname.startsWith("/CheckOut")) setFooterMargin("150px");
        else if (location.pathname === "/PaymentSummary") setFooterMargin("150px");
        else setFooterMargin("60px");
      } else {
        setFooterMargin("0px");
      }
    };

    updateMargin();
    window.addEventListener("resize", updateMargin);
    return () => window.removeEventListener("resize", updateMargin);
  }, [location.pathname]);

  // ✅ Handle responsive accordion for mobile
  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // ✅ Animation variants
  const listVariants = {
    hidden: { height: 0, opacity: 0 },
    visible: { height: "auto", opacity: 1 },
    exit: { height: 0, opacity: 0 },
  };

  // ✅ Reveal animation observer
  useEffect(() => {
    const root = footerRef.current;
    if (!root) return;
    const reveals = Array.from(root.querySelectorAll(".reveal"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("show");
        });
      },
      { threshold: 0.15 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => reveals.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <>
      <TestimonialSlider />
      <ServiceSection />
      <CallToAction />

      {/* ✅ Footer */}
      <footer
        ref={footerRef}
        className="web-footer"
        style={{
          paddingTop: "var(--space-lg)",
          position: "relative",
          backgroundImage: `url(${background})`,
          backgroundSize: "contain",
          backgroundPosition: "bottom",
          backgroundRepeat: "no-repeat",
          fontFamily: "'Montserrat', sans-serif",
          overflow: "hidden",
          marginBottom: footerMargin, // 👈 dynamic margin fixed
          transition: "margin-bottom 0.3s ease",
        }}
      >
        {/* Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(65, 190, 111, 0.15)",
            zIndex: 1,
          }}
        ></div>

        <div className="container px-3 px-md-5 py-5" style={{ position: "relative", zIndex: 5 }}>
          <div className="row gy-5">
            {/* ✅ Company Info */}
            <div className="col-lg-4 col-md-6 text-start reveal from-up delay-1">
              <h2
                style={{
                  fontSize: "var(--font-xl)",
                  fontWeight: "800",
                  marginBottom: "var(--space-sm)",
                  color: "var(--color-text)",
                }}
              >
                Dharaj Agriculture
              </h2>
              <p
                className="mb-3"
                style={{
                  fontSize: "var(--font-sm)",
                  lineHeight: 1.7,
                  color: "var(--color-muted)",
                }}
              >
                Dharaj Agriculture Technologies Pvt. Ltd. provides innovative
                agricultural solutions dedicated to enhancing productivity and
                sustainability.
              </p>
              <div style={{ fontSize: "var(--font-sm)", color: "var(--color-text)" }}>
                <div className="d-flex align-items-center mb-1">
                  <FaMapMarkerAlt className="me-2" />
                  <span>
                    <strong>Corporate Office:</strong> Sector 44, Gurugram
                  </span>
                </div>
                <div className="d-flex align-items-center mb-1">
                  <FaMapMarkerAlt className="me-2" />
                  <span>
                    <strong>Registered Office:</strong> IMT Manesar, Gurugram
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <FaPhone className="me-2" />
                  <span>
                    <strong>Grievance Officer:</strong> Suyash Gupta
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ Services */}
            <div className="col-lg-2 col-md-6 col-12 text-start reveal from-left delay-2 footer-section">
              <h5
                className="footer-title"
                onClick={() => isMobile && toggleSection("services")}
                style={{ cursor: isMobile ? "pointer" : "default" }}
              >
                Services{" "}
                {isMobile &&
                  (openSection === "services" ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  ))}
              </h5>

              <AnimatePresence>
                {(!isMobile || openSection === "services") && (
                  <motion.ul
                    className="list-unstyled mt-3 footer-list"
                    variants={listVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    {["Shop", "Track Order", "Our Store", "Blog", "Corporate Info", "Contact"].map(
                      (item, i) => (
                        <li key={i}>
                          <a href="#" className="footer-link">
                            {item}
                          </a>
                        </li>
                      )
                    )}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>

            {/* ✅ Policies */}
            <div className="col-lg-2 col-md-6 col-12 text-start reveal from-right delay-3 footer-section">
              <h5
                className="footer-title"
                onClick={() => isMobile && toggleSection("policies")}
                style={{ cursor: isMobile ? "pointer" : "default" }}
              >
                Policies{" "}
                {isMobile &&
                  (openSection === "policies" ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  ))}
              </h5>

              <AnimatePresence>
                {(!isMobile || openSection === "policies") && (
                  <motion.ul
                    className="list-unstyled mt-3 footer-list"
                    variants={listVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    {["Privacy", "Shipping", "Refund", "Terms", "Sitemap"].map((item, i) => (
                      <li key={i}>
                        <a href="#" className="footer-link">
                          {item}
                        </a>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>

            {/* ✅ Help & Newsletter */}
            <div className="col-lg-4 col-md-6 col-12 text-start reveal from-up delay-4">
              <h5 style={{ fontWeight: 700, fontSize: "var(--font-base)", color: "var(--color-text)" }}>
                Need Help?
              </h5>
              <h6
                className="mt-3"
                style={{
                  fontSize: "var(--font-sm)",
                  fontWeight: 600,
                  color: "var(--color-text)",
                }}
              >
                Subscribe to Our Newsletter
              </h6>
              <div className="d-flex gap-2 mt-2 flex-wrap">
                <input
                  type="email"
                  placeholder="Enter email"
                  className="form-control"
                  style={{
                    maxWidth: 220,
                    fontSize: "var(--font-sm)",
                    borderRadius: "var(--radius-lg)",
                  }}
                />
                <button
                  className="btn fw-bold"
                  style={{
                    background: "var(--color-secondary)",
                    color: "var(--color-primary)",
                  }}
                >
                  Subscribe
                </button>
              </div>

              <div className="mt-3">
                <button
                  className="btn fw-bold px-4 py-2"
                  style={{
                    backgroundColor: "var(--color-secondary)",
                    color: "var(--color-primary)",
                    borderRadius: "50px",
                  }}
                >
                  Contact Us
                </button>
              </div>

              <div className="d-flex gap-3 mt-4">
                {[FaFacebook, FaInstagram, FaTwitter].map((Icon, idx) => (
                  <button
                    key={idx}
                    className="icon-btn"
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      background: "var(--color-secondary)",
                      border: "none",
                    }}
                  >
                    <Icon size={18} style={{ color: "var(--color-primary)" }} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ✅ Rewards */}
          <div className="border-top mt-4 pt-3 reveal from-up delay-5">
            <div className="d-flex flex-column flex-md-row align-items-start justify-content-between gap-3">
              <span style={{ fontWeight: 600, color: "var(--color-text)" }}>
                Dharaj Agriculture Rewards
              </span>
              <div
                className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                style={{
                  backgroundColor: "var(--color-secondary)",
                  color: "var(--color-primary)",
                  fontSize: "var(--font-sm)",
                  fontWeight: 500,
                }}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                  style={{
                    width: 24,
                    height: 24,
                    backgroundColor: "#ff6b35",
                    fontSize: 12,
                  }}
                >
                  P
                </div>
                Get Upto 17% Off Using POPcoins →
              </div>
            </div>
          </div>

          <p className="mt-4 small text-start" style={{ color: "var(--color-muted)" }}>
            © 2025 Dharaj Agriculture Technologies Pvt. Ltd. All Rights Reserved.
          </p>
        </div>

        {/* ✅ Inline CSS */}
        <style>{`
          .web-footer {
            text-align: center;
            padding: 20px;
            background-color: #f8f8f8;
          }

          .footer-content h4 {
            margin-bottom: 10px;
            color: #222;
          }

          .footer-content p {
            font-size: 14px;
            color: #555;
            margin: 0;
          }

          .footer-links {
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-bottom: 10px;
          }

          .footer-link {
            color: var(--color-muted);
            text-decoration: none;
            transition: all 0.3s ease;
          }
          .footer-link:hover {
            color: var(--color-text);
          }

          .icon-btn:hover {
            transform: scale(1.1);
            transition: all 0.2s ease;
          }
        `}</style>
      </footer>
    </>
  );
};

export default Webfooter;
